import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import EN from './utils/en';
import HI from './utils/hi';

// Backend stores 'english' | 'hindi'; i18next uses 'en' | 'hi'.
export const LANG_TO_LOCALE = { english: 'en', hindi: 'hi' };
export const LOCALE_TO_LANG = { en: 'english', hi: 'hindi' };
export const SUPPORTED_LOCALES = ['en', 'hi'];
export const LOCALE_STORAGE_KEY = 'app_locale';

// Resolve the initial locale: persisted choice → default 'en'.
const storedLocale = (() => {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return SUPPORTED_LOCALES.includes(v) ? v : 'en';
  } catch (e) {
    return 'en';
  }
})();

i18n.use(initReactI18next).init({
  // en.js / hi.js are nested objects with identical key structure; loaded as one
  // "translation" namespace and addressed via dot-paths (keySeparator '.').
  // One dictionary per language for the whole platform (single namespace).
  resources: {
    en: { translation: EN },
    hi: { translation: HI },
  },
  lng: storedLocale,
  fallbackLng: 'en',
  keySeparator: '.',
  nsSeparator: false, // keys contain no ':'; namespace passed via t(key, { ns }) or useTranslation(ns)
  returnNull: false,
  returnEmptyString: false,
  interpolation: {
    escapeValue: false, // React already escapes
    // Our strings use single-brace placeholders: "Hello {name}".
    prefix: '{',
    suffix: '}',
  },
  react: { useSuspense: false },
});

/**
 * Switch the active UI language and persist it. Accepts a locale ('en'|'hi').
 */
export const setLocale = (locale) => {
  const next = SUPPORTED_LOCALES.includes(locale) ? locale : 'en';
  try { localStorage.setItem(LOCALE_STORAGE_KEY, next); } catch (e) { /* ignore */ }
  return i18n.changeLanguage(next);
};

/** Set language from a backend `user.language` value ('english'|'hindi'). */
export const setLocaleFromUserLanguage = (language) => {
  const locale = LANG_TO_LOCALE[language] || 'en';
  return setLocale(locale);
};

/**
 * Reset the UI language on logout. Clears the persisted locale and returns to
 * the default so the next user who logs in does not inherit the previous
 * user's language before their own `user.language` is applied.
 */
export const resetLocale = () => {
  try { localStorage.removeItem(LOCALE_STORAGE_KEY); } catch (e) { /* ignore */ }
  return i18n.changeLanguage('en');
};

export const getCurrentLanguage = () => LOCALE_TO_LANG[i18n.language] || 'english';

export default i18n;
